import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, InputLabel, Typography, useMediaQuery } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import { SxProps } from '@mui/system';

export enum FILE_TYPE {
    IMAGE = 'image',
    CSV = 'csv',
}

interface ImageUploaderProps {
    handleFileUpload: (file: File | null) => void;
    type: FILE_TYPE.IMAGE | FILE_TYPE.CSV;
    externalErrorMessage?: string;
    secondaryText?: string;
    sx?: SxProps;
}

const FileUploader = ({ handleFileUpload, type, externalErrorMessage = '', secondaryText, sx }: ImageUploaderProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isDragging, setIsDragging] = useState<boolean>(false);

    useEffect(() => {
        setErrorMessage(externalErrorMessage);
    }, [externalErrorMessage]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (inputRef.current) {
            // needs to delete the file to avoid issues with html caching
            inputRef.current.value = '';
        }

        validateAndUpload(file);
    };

    const validateAndUpload = (file: File | undefined) => {
        setErrorMessage('');

        if (file) {
            if (type === FILE_TYPE.IMAGE) {
                const validFormats = ['image/jpeg', 'image/png'];
                const maxSizeInBytes = 10 * 1000 * 1000;

                if (!validFormats.includes(file.type) || file.size >= maxSizeInBytes) {
                    setErrorMessage(t('image-format-validation'));
                    return;
                }
            } else if (type === FILE_TYPE.CSV) {
                const validFormat = 'text/csv';

                if (file.type !== validFormat) {
                    setErrorMessage(t('users-upload-csv-error'));
                    return;
                }
            }

            handleFileUpload(file);
        }
    };

    const fileAcceptType = useMemo(() => {
        if (type === FILE_TYPE.IMAGE) return 'image/jpeg, image/png';
        if (type === FILE_TYPE.CSV) return '.csv';

        return '*';
    }, [type]);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files?.[0];
        validateAndUpload(file);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                border: '1px dashed',
                borderColor: isDragging ? 'common.primaryHover' : 'common.primary',
                padding: isMobile ? '12px' : '16px',
                borderRadius: 2,
                cursor: 'pointer',
                width: '100%',
                height: '175px',
                '&:hover': {
                    cursor: 'pointer',
                    borderColor: 'common.primaryHover',
                    '& .upload-icon': {
                        color: 'common.primaryHover',
                    },
                    '& .upload-text': {
                        color: 'common.primaryHover',
                    },
                    '& .upload-label': {
                        cursor: 'pointer',
                    },
                },
                ...sx,
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid='dropzone'
        >
            <InputLabel
                htmlFor='file-upload'
                className='upload-label'
                sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: isMobile ? '6px' : '8px' }}
            >
                <input
                    onChange={handleFileChange}
                    accept={fileAcceptType}
                    id='file-upload'
                    type='file'
                    style={{ display: 'none' }}
                    ref={inputRef}
                />
                <UploadFileIcon className='upload-icon' sx={{ color: 'common.primary', fontSize: '24px' }} />
                <Box sx={{ display: 'flex', gap: '6px' }}>
                    <Typography className='upload-text' sx={{ color: 'common.primary', textDecoration: 'underline' }}>
                        {t('click-to-upload')}
                    </Typography>
                    <Typography sx={{ fontSize: '14px' }}>
                        {t('or')} {t('drag-and-drop')}
                    </Typography>
                </Box>
                <Typography
                    color='textSecondary'
                    sx={{ textWrap: 'wrap', textAlign: 'center', lineHeight: '20px', fontSize: '14px' }}
                >
                    {type === 'image' && !secondaryText && t('image-allowed-formats')}
                    {secondaryText}
                </Typography>
                {errorMessage && (
                    <Typography
                        color='common.system.error.main'
                        sx={{ fontSize: '12px', textWrap: 'wrap', textAlign: 'center' }}
                    >
                        {errorMessage}
                    </Typography>
                )}
            </InputLabel>
        </Box>
    );
};

export default FileUploader;
